import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { requestPermissionAndToken } from "../firebase";
import { api } from "../services/api";
import { signInWithGoogle } from "../firebaseAuth";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";

export default function Login() {
  const nav = useNavigate();
  const { login, loginWithIdToken } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true); // Default to true for automatic remember me
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Check if user was redirected due to session expiry
  useEffect(() => {
    const wasLoggedIn = localStorage.getItem("wasLoggedIn");
    if (wasLoggedIn === "true" && !localStorage.getItem("token")) {
      setSessionExpired(true);
      localStorage.removeItem("wasLoggedIn");
      
      // Auto-hide message after 10 seconds
      setTimeout(() => setSessionExpired(false), 10000);
    }
  }, []);

  async function registerAfterLogin() {
    const token = await requestPermissionAndToken();
    if (token) {
      await api("/save-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      localStorage.setItem("push_registered_v1", "1");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setErr("");
    
    try {
      console.log("🔐 Starting login process...");
      await login(email, password, rememberMe);
      console.log("✅ Login successful, checking user data...");
      
      // Wait for user to be set in localStorage
      let attempts = 0;
      while (!localStorage.getItem("user") && attempts < 20) {
        console.log("⏳ Waiting for user data... attempt", attempts + 1);
        await new Promise(resolve => setTimeout(resolve, 50));
        attempts++;
      }
      
      const savedUser = localStorage.getItem("user");
      console.log("📦 User data in localStorage:", savedUser ? "FOUND" : "MISSING");
      
      if (!savedUser) {
        console.error("❌ User data not saved after login!");
        throw new Error("User data not available");
      }
      
      await registerAfterLogin();
      console.log("✅ Push token registered, navigating to dashboard...");
      nav("/dashboard");
    } catch (e) {
      console.error("❌ Login failed:", e);
      setErr(e?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleSocialSignIn(getIdToken) {
    setLoading(true);
    setErr("");
    try {
      const idToken = await getIdToken();
      await loginWithIdToken(idToken, rememberMe);
      let attempts = 0;
      while (!localStorage.getItem("user") && attempts < 20) {
        await new Promise((r) => setTimeout(r, 50));
        attempts++;
      }
      const savedUser = localStorage.getItem("user");
      if (!savedUser) throw new Error("User data not available");
      nav("/dashboard");
      registerAfterLogin().catch(() => {});
    } catch (e) {
      setErr(e?.message || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#070b14] flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/logo-icon.png" alt="SiteScope Logo" className="w-12 h-12" />
            <h1 className="text-3xl font-bold text-white">SiteScope</h1>
          </div>
          <p className="text-sm text-gray-500">24/7 Website Monitoring</p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        {/* Session Expired Notice */}
        {sessionExpired && (
          <div className="mb-4 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-amber-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-amber-300">
                  Session Expired
                </h3>
                <p className="text-sm text-amber-400/80 mt-1">
                  Your session has expired or could not load your data. Please log in again to continue.
                </p>
              </div>
              <button
                onClick={() => setSessionExpired(false)}
                className="ml-3 text-amber-400 hover:text-amber-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        <Card className="sm:px-10">
          <Card.Header>
            <Card.Title className="text-center">Sign in to your account</Card.Title>
            <Card.Description className="text-center">
              Monitor your websites around the clock
            </Card.Description>
          </Card.Header>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-400">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none block w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-xl placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 sm:text-sm transition-all"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-400">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none block w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-xl placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 sm:text-sm transition-all"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-white/20 text-emerald-500 focus:ring-emerald-500 bg-white/5"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-400">
                Keep me signed in for 90 days
              </label>
            </div>

            {err && (
              <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4">
                <div className="text-sm text-red-400">{err}</div>
              </div>
            )}

            <div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={loading}
                className="w-full"
              >
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </div>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-[#0b1120] text-gray-500">Or continue with</span>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => handleSocialSignIn(signInWithGoogle)}
                disabled={loading}
                className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-white/10 rounded-xl bg-white/5 text-gray-300 text-sm font-medium hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#070b14] focus:ring-emerald-500 disabled:opacity-40 transition-all"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </div>
          </form>

          <Card.Footer>
            <div className="flex flex-col space-y-3 sm:flex-row sm:justify-between sm:space-y-0">
              <Link
                to="/forgot-password"
                className="text-sm text-emerald-400 hover:text-emerald-300 text-center sm:text-left"
              >
                Forgot your password?
              </Link>
              <span className="text-sm text-gray-500 text-center sm:text-right">
                Don't have an account?{" "}
                <Link to="/register" className="text-emerald-400 hover:text-emerald-300">
                  Sign up
                </Link>
              </span>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
}
